import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Chip, Stack, Typography } from '@mui/material';
import { LinkedinTypes } from 'enums';
import PaiListItem from 'features/ui/base-components/pai-list-item';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import StepActions from '../step-actions';
import StepHint from '../step-hint';
import StepLayout from '../step-layout';

interface Props {
  statusMessage?: string;
  liProductType: LinkedinTypes;
  verificationCode: string;
  verificationLoading: boolean;
  twoFaScreenshot?: string | null;

  handleVerificationCodeChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleVerifyLinkedinAccountFromAppSync(mode?: 'pin' | 'approved'): void;

  onResetAccount?(): void;
}

/**
 * The structured half of `statusMessage`.
 *
 * The backend used to send prose only, so this screen had to guess what was going on from
 * the wording — and guessed wrong in the one case that matters: for an app-push challenge
 * with no code option, it offered a code field for a code LinkedIn never sent. These flags
 * say it outright.
 */
interface TwoFaStatus {
  method?: string;
  userAction?: string;
  message?: string;
  /** We are actively waiting for the approval right now. */
  awaitingApproval?: boolean;
  /** A code has actually been sent and can be typed in. */
  codeAvailable?: boolean;
  /** LinkedIn offers a "send code" route for this challenge at all. */
  canRequestCode?: boolean;
  /** When the current wait runs out, ISO string. */
  approvalDeadlineAt?: string;
}

/** Counts down to `deadlineAt`, or null once it has passed / is absent. */
const useRemainingSeconds = (deadlineAt?: string): number | null => {
  const [remaining, setRemaining] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!deadlineAt) {
      setRemaining(null);
      return;
    }
    const target = new Date(deadlineAt).getTime();
    if (Number.isNaN(target)) {
      setRemaining(null);
      return;
    }
    const tick = () => setRemaining(Math.max(0, Math.round((target - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadlineAt]);

  return remaining;
};

const Enter2fa: React.FC<Props> = ({
  statusMessage,
  liProductType,
  verificationCode,
  verificationLoading,
  twoFaScreenshot,
  handleVerificationCodeChange,
  handleVerifyLinkedinAccountFromAppSync,
  onResetAccount,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const isVerificationCodeEmpty = verificationCode.trim().length === 0;
  const parsedStatusMessage = React.useMemo(() => {
    if (!statusMessage) return null;
    try {
      return JSON.parse(statusMessage) as TwoFaStatus;
    } catch {
      return null;
    }
  }, [statusMessage]);
  const twoFaMethod = parsedStatusMessage?.method;
  const isAppPush = twoFaMethod === 'app_push';

  // Absent flags mean an older record written before the backend sent them; assume a code
  // route exists, which is what this screen did before.
  const canRequestCode = parsedStatusMessage?.canRequestCode !== false;
  const codeAvailable = parsedStatusMessage?.codeAvailable !== false;
  const isAwaitingApproval = parsedStatusMessage?.awaitingApproval === true;
  const remainingSeconds = useRemainingSeconds(
    isAwaitingApproval ? parsedStatusMessage?.approvalDeadlineAt : undefined,
  );

  // The dead end worth naming: the app tap was the only way through and the wait ran out.
  // Offering a code field here is what left people typing into a box for a code that was
  // never sent — the honest move is to say so and let them start again.
  const approvalMissedWithoutCode =
    isAppPush && !isAwaitingApproval && !canRequestCode && !codeAvailable;

  const [showCodeEntry, setShowCodeEntry] = React.useState(!isAppPush);

  React.useEffect(() => {
    setShowCodeEntry(approvalMissedWithoutCode ? false : !isAppPush);
  }, [isAppPush, approvalMissedWithoutCode, statusMessage]);

  /**
   * Recruiter und Sales Navigator brauchen zwei Bestätigungen: eine für die normale
   * LinkedIn-App, eine für die Produkt-App. Das Gerätevertrauen hängt am Cookie-Jar der
   * jeweiligen App, nicht am Gerät. Wer das nicht weiß, legt nach dem ersten Code das
   * Handy weg — und die zweite Frist läuft ab.
   */
  const hasSecondRound = liProductType === LinkedinTypes.RECRUITER || liProductType === LinkedinTypes.SALES_NAV;
  const productAppLabel = liProductType === LinkedinTypes.RECRUITER ? 'Recruiter' : 'Sales Navigator';

  const isPushMode = isAppPush && !showCodeEntry;

  const instructionText =
    parsedStatusMessage?.userAction ||
    (isAppPush ? t('appSyncApprovePushInstruction') : t('appSyncVerifyLinkedinAccountInstruction1'));

  return (
    <StepLayout
      title={isPushMode ? t('appSyncApproveLinkedinPush') : t('appSyncTwoFaTitle', 'Kurz bestätigen, dass du es bist')}
      description={
        isPushMode ? (
          <span className={classes.preserveLineBreaks}>{instructionText}</span>
        ) : (
          t(
            'appSyncTwoFaDescription',
            'Öffne deine Authenticator-App und gib den 6-stelligen Code ein. LinkedIn nutzt ihn, um dein Gerät dauerhaft zu vertrauen — danach musst du das nicht mehr tun.',
          )
        )
      }
      footer={
        isPushMode ? (
          approvalMissedWithoutCode ? (
            <StepActions
              primaryText={t('appSyncRestartConnection', 'Start again')}
              onPrimary={() => onResetAccount?.()}
              primaryDisabled={!onResetAccount}
            />
          ) : (
            <StepActions
              primaryText={t('appSyncApprovedInLinkedinApp')}
              primaryLoading={verificationLoading}
              onPrimary={() => handleVerifyLinkedinAccountFromAppSync('approved')}
              secondaryText={t('appSyncNeedVerificationCode')}
              onSecondary={() => setShowCodeEntry(true)}
            />
          )
        ) : (
          <StepActions
            primaryText={t('appSyncConfirm', 'Bestätigen')}
            primaryLoading={verificationLoading}
            primaryDisabled={isVerificationCodeEmpty}
            onPrimary={() => handleVerifyLinkedinAccountFromAppSync('pin')}
            secondaryText={isAppPush ? t('appSyncBackToPushApproval') : undefined}
            onSecondary={isAppPush ? () => setShowCodeEntry(false) : undefined}
          />
        )
      }
    >
      <Stack gap={2}>
        <Stack direction={'row'}>
          <Chip
            className={classes.methodChip}
            icon={isPushMode ? <NotificationsActiveIcon /> : <PhoneIphoneIcon />}
            label={isPushMode ? t('appSyncTwoFaViaApp', 'Freigabe in der LinkedIn-App') : t('appSyncTwoFaViaCode', 'Code aus deiner App')}
          />
        </Stack>

        {twoFaScreenshot ? (
          <Stack direction={'row'} justifyContent={'center'}>
            <img className={classes.twoFaScreenshot} src={twoFaScreenshot} alt={'LinkedIn 2FA challenge'} />
          </Stack>
        ) : null}

        {isPushMode ? (
          <Stack gap={1.5}>
            <PaiListItem text={t('appSyncApprovePushInstruction1')} size={'large'} disablePadding={true} />
            <PaiListItem text={t('appSyncApprovePushInstruction2')} size={'large'} disablePadding={true} />
            <PaiListItem text={t('appSyncApprovePushInstruction3')} size={'large'} highlight={true} disablePadding={true} />
            {remainingSeconds !== null && remainingSeconds > 0 && (
              // Without this the screen looks identical whether we are still listening or
              // long since gave up — which is why people kept tapping approve too late.
              <Typography className={classes.waitingHint}>
                {t('appSyncPushWaitingCountdown', 'We are listening for your confirmation — {{seconds}}s left', {
                  seconds: remainingSeconds,
                })}
              </Typography>
            )}
            {approvalMissedWithoutCode && (
              <Typography className={classes.expiredHint}>
                {t(
                  'appSyncPushWindowExpired',
                  'The confirmation did not arrive in time. LinkedIn does not offer a code for this account, so the app confirmation is the only way — please start the connection again and keep your phone ready.',
                )}
              </Typography>
            )}
          </Stack>
        ) : (
          <>
            <PaiTextField
              value={verificationCode}
              placeholder={t('enterLinkedinVerificationCodeHere')}
              style={{ maxWidth: '260px' }}
              onChange={handleVerificationCodeChange}
            />
            <Stack gap={1}>
              <PaiListItem text={t('appSyncVerifyLinkedinAccountInstruction1')} size={'large'} disablePadding={true} />
              <PaiListItem text={t('appSyncVerifyLinkedinAccountInstruction2')} size={'large'} disablePadding={true} />
              <PaiListItem text={t('appSyncVerifyLinkedinAccountInstruction3')} size={'large'} highlight={true} disablePadding={true} />
            </Stack>
          </>
        )}

        {hasSecondRound ? (
          <StepHint
            tone={'warning'}
            icon={<WarningAmberIcon />}
            text={t(
              'appSyncTwoFaSecondRoundHint',
              'Danach folgt noch eine zweite Bestätigung für die {{productApp}}-App — dann bist du für beide fertig.',
              { productApp: productAppLabel },
            )}
          />
        ) : null}
      </Stack>
    </StepLayout>
  );
};

const useStyles = makeStyles()((theme) => ({
  methodChip: {
    background: theme.palette.backgrounds.main,
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 'bold',
    '& .MuiChip-icon': {
      color: theme.palette.primary.main,
      fontSize: theme.typography.pxToRem(15),
    },
  },
  preserveLineBreaks: {
    whiteSpace: 'pre-line',
  },
  waitingHint: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  expiredHint: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.warning.main,
  },
  twoFaScreenshot: {
    maxWidth: '100%',
    maxHeight: '260px',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.divider}`,
    objectFit: 'contain' as const,
  },
}));

export default Enter2fa;
